<template>
  <div class="dc-lock-overlay">
    <div class="dc-lock-card">
      <div class="dc-lock-icon">
        <i class="fas fa-lock"></i>
      </div>

      <p class="dc-lock-title">หน้าจอถูกล็อก</p>
      <p v-if="!showRoster" class="dc-lock-user">{{ displayName }}</p>

      <!-- Only when more than one person is parked here. A single-name roster
           is the same information as the line above, in more pixels. -->
      <div v-if="showRoster" class="dc-lock-roster">
        <button
          v-for="member in roster"
          :key="member.uid"
          type="button"
          class="dc-lock-chip"
          :class="{ 'dc-lock-chip--active': member.uid === selectedUid }"
          :disabled="busy"
          @click="selectUser(member.uid)"
        >
          {{ member.name || `#${member.uid}` }}
        </button>
      </div>

      <!-- PIN is the primary method whenever the user has one: it is the fast
           path at a counter, and it is the credential whose leak costs least. -->
      <form v-if="method === 'pin'" autocomplete="off" @submit.prevent="submitPin">
        <label class="dc-lock-label" for="dc-lock-pin">PIN 6 หลัก</label>
        <!-- Not type="password" where CSS can mask instead: every password
             manager finds its fill target by input[type=password], and Chrome
             has ignored autocomplete="off" on those for years. See
             utils/pinField.js for the fallback when CSS cannot mask. -->
        <input
          id="dc-lock-pin"
          ref="pinInput"
          v-model="pin"
          v-bind="pinAttrs"
          :type="pinType"
          inputmode="numeric"
          maxlength="6"
          name="dc-unlock-code"
          class="dc-lock-input dc-lock-input--pin"
          :class="{ 'dc-masked': maskWithCss }"
          :disabled="busy"
          @input="onPinInput"
        />
        <p v-if="error" class="dc-lock-error">{{ error }}</p>
        <!-- Submits itself on the sixth digit. Kept as the keyboard- and
             screen-reader-reachable path, and as the way back if a paste lands
             six digits without firing the auto-submit. -->
        <button
          type="submit"
          class="dc-lock-btn dc-lock-btn--primary"
          :disabled="busy || pin.length !== 6"
        >
          {{ busy ? "กำลังตรวจสอบ..." : "ปลดล็อก" }}
        </button>
        <button type="button" class="dc-lock-link" @click="usePassword">
          ลืม PIN? ใช้รหัสผ่าน
        </button>
      </form>

      <form v-else @submit.prevent="submitPassword">
        <label class="dc-lock-label" for="dc-lock-password">รหัสผ่าน</label>
        <!-- autocomplete="new-password" so the browser does not offer a
             colleague's saved password into a shared counter machine. This
             reduces the exposure; it does not close it (spec 09 §4.3, §7). -->
        <input
          id="dc-lock-password"
          ref="passwordInput"
          v-model="password"
          type="password"
          autocomplete="new-password"
          class="dc-lock-input"
          :disabled="busy"
        />
        <p v-if="error" class="dc-lock-error">{{ error }}</p>
        <button
          type="submit"
          class="dc-lock-btn dc-lock-btn--primary"
          :disabled="busy || !password"
        >
          ปลดล็อก
        </button>
        <button
          v-if="hasPin && !pinDisabled"
          type="button"
          class="dc-lock-link"
          @click="usePin"
        >
          กลับไปใช้ PIN
        </button>
      </form>

      <!-- Without this, whoever takes over the machine is stuck at a
           colleague's lock screen, and the workaround is learning the
           colleague's password — the outcome the lock exists to prevent. -->
      <button
        type="button"
        class="dc-lock-link dc-lock-link--switch"
        :disabled="busy"
        @click="switchAccount"
      >
        เข้าใช้ด้วยบัญชีอื่น
      </button>

      <!-- The control that most limits the risk of keeping a roster: it is
           what empties it. Belongs in the closing-up routine. -->
      <button
        v-if="showRoster"
        type="button"
        class="dc-lock-link dc-lock-link--endshift"
        :disabled="busy"
        @click="confirmEndShift"
      >
        จบกะ — ออกจากระบบทุกคน
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  pinInputType,
  pinIgnoreAttrs,
  supportsCssTextSecurity,
} from "@/utils/pinField";
import { clearLockState, announceUserSwitch } from "@/utils/idleLock";

export default {
  name: "LockOverlay",
  data() {
    return {
      method: "pin",
      pin: "",
      password: "",
      error: "",
      busy: false,
      pinDisabled: false,
      pinType: pinInputType(),
      pinAttrs: pinIgnoreAttrs(),
      maskWithCss: supportsCssTextSecurity(),
      roster: [],
      selectedUid: null,
    };
  },
  computed: {
    ...mapGetters("moduleUser", [
      "getUserInfo",
      "getHasPin",
      "getUserSwitchingEnabled",
    ]),
    showRoster() {
      return this.getUserSwitchingEnabled && this.roster.length > 1;
    },
    // Entering a PIN for anyone but the current user is a SWITCH, and goes to
    // a different endpoint with different failure handling.
    isSwitching() {
      return (
        this.selectedUid !== null &&
        this.selectedUid !== (this.getUserInfo || {}).uid
      );
    },
    hasPin() {
      return this.getHasPin;
    },
    displayName() {
      const info = this.getUserInfo || {};
      const name = [info.firstNameTh, info.lastNameTh]
        .filter(Boolean)
        .join(" ")
        .trim();
      return name || info.email || "";
    },
  },
  created() {
    this.method = this.getHasPin ? "pin" : "password";
    this.selectedUid = (this.getUserInfo || {}).uid ?? null;
    this.loadRoster();
  },
  mounted() {
    this.focusActiveField();
  },
  methods: {
    focusActiveField() {
      this.$nextTick(() => {
        const el =
          this.method === "pin" ? this.$refs.pinInput : this.$refs.passwordInput;
        if (el && el.focus) el.focus();
      });
    },
    onPinInput() {
      // Digits only, so a stray character cannot make a 6-length string that
      // the server will reject anyway.
      this.pin = String(this.pin).replace(/\D/g, "").slice(0, 6);
      this.error = "";

      // Submit on the sixth digit — no button press. The busy guard is what
      // keeps a fast typist, or a paste that fires input twice, from spending
      // two of the five attempts on one entry.
      if (this.pin.length === 6 && !this.busy) this.submitPin();
    },
    usePin() {
      this.method = "pin";
      this.error = "";
      this.password = "";
      this.focusActiveField();
    },
    usePassword() {
      this.method = "password";
      this.error = "";
      this.pin = "";
      this.focusActiveField();
    },
    async submitPin() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        if (this.isSwitching) {
          const data = await this.$store.dispatch(
            "moduleUser/switchDeviceUser",
            { uid: this.selectedUid, pin: this.pin }
          );
          return this.completeSwitch(data);
        }
        await this.$store.dispatch("moduleUser/unlockWithPin", this.pin);
        this.finish();
      } catch (err) {
        if (this.isSwitching) return this.handleSwitchError(err);
        const data = (err && err.response && err.response.data) || {};
        this.pin = "";
        if (data.reason === "disabled") {
          this.pinDisabled = true;
          this.error = "PIN ถูกระงับชั่วคราว กรุณาปลดล็อกด้วยรหัสผ่าน";
          this.usePassword();
        } else if (data.reason === "no_pin") {
          this.error = "บัญชีนี้ยังไม่ได้ตั้ง PIN กรุณาใช้รหัสผ่าน";
          this.usePassword();
        } else {
          const left = data.remainingAttempts;
          this.error =
            typeof left === "number"
              ? `PIN ไม่ถูกต้อง เหลืออีก ${left} ครั้ง`
              : "PIN ไม่ถูกต้อง";
        }
      } finally {
        this.busy = false;
      }
    },
    async submitPassword() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.$store.dispatch("moduleUser/unlockWithPassword", this.password);
        // A password unlock re-enables a PIN the server had disabled.
        this.pinDisabled = false;
        this.finish();
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.password = "";
        this.error =
          status === 423
            ? "บัญชีถูกล็อกชั่วคราวจากการกรอกรหัสผ่านผิดหลายครั้ง"
            : "รหัสผ่านไม่ถูกต้อง";
      } finally {
        this.busy = false;
      }
    },
    finish() {
      this.pin = "";
      this.password = "";
      this.error = "";
      this.$emit("unlocked");
    },
    async loadRoster() {
      if (!this.getUserSwitchingEnabled) return;
      try {
        this.roster = await this.$store.dispatch("moduleUser/loadDeviceSessions");
      } catch (e) {
        // A roster is a convenience. Failing to load one must not leave the
        // machine unusable — the single-user lock screen still works.
        this.roster = [];
      }
    },
    selectUser(uid) {
      this.selectedUid = uid;
      this.pin = "";
      this.error = "";
      // Switching always goes through the PIN; the password path unlocks the
      // CURRENT session and cannot authenticate somebody else.
      if (uid !== (this.getUserInfo || {}).uid) this.method = "pin";
      this.focusActiveField();
    },
    completeSwitch(data) {
      const token = data && data.data && data.data.accessToken;
      if (token) localStorage.setItem("access_token", token);

      // Order matters. announceUserSwitch BEFORE clearLockState: both fire
      // `storage` in sibling tabs, and clearing the lock key alone reads as
      // "unlock" — siblings would release into the PREVIOUS user's rendered
      // state and could paint a frame of it before the epoch event lands.
      announceUserSwitch();
      clearLockState();

      // A full reload, not a state swap. The lock deliberately preserves every
      // Vuex module because it is the same person coming back; on a switch
      // that leaves the previous user's identity and their patients' data live
      // under the new user's token.
      window.location.reload();
    },
    handleSwitchError(err) {
      const status = err && err.response && err.response.status;
      const data = (err && err.response && err.response.data) || {};
      this.pin = "";

      // Route on the STATUS, never the reason: every authorization refusal
      // shares one body precisely so it cannot be used to probe account state.
      if (status === 409) {
        this.error = "กรุณาเข้าสู่ระบบด้วยรหัสผ่านก่อน";
        return;
      }
      if (status === 401 && typeof data.remainingAttempts === "number") {
        this.error =
          data.reason === "disabled"
            ? "PIN ถูกระงับชั่วคราว ต้องเข้าสู่ระบบด้วยรหัสผ่าน"
            : `PIN ไม่ถูกต้อง เหลืออีก ${data.remainingAttempts} ครั้ง`;
        return;
      }
      // A 401 with no remainingAttempts is an expired Bearer, not a bad PIN.
      this.error = "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
    },
    async confirmEndShift() {
      const ok = await this.$bvModal.msgBoxConfirm(
        "ทุกคนจะต้องเข้าสู่ระบบด้วยรหัสผ่านใหม่ในครั้งถัดไป",
        {
          title: "จบกะ",
          okTitle: "จบกะ",
          cancelTitle: "ยกเลิก",
          okVariant: "danger",
        }
      );
      if (!ok) return;

      this.busy = true;
      try {
        await this.$store.dispatch("moduleUser/endShift");
      } catch (e) {
        // Even a failed end-shift should still log this machine out; the rows
        // expire on their own within the hard cap.
      }
      await this.switchAccount();
    },
    async switchAccount() {
      this.busy = true;
      try {
        await this.$store.dispatch("moduleAuth/userLogout");
      } catch (e) {
        // A failed logout call must not trap the next person on this screen.
      }
      this.$emit("switch-account");
    },
  },
};
</script>

<style scoped>
.dc-lock-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.92);
}

.dc-lock-card {
  width: 100%;
  max-width: 360px;
  padding: 32px 28px;
  border-radius: 12px;
  background: #fff;
  text-align: center;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35);
}

.dc-lock-icon {
  font-size: 28px;
  color: #64748b;
  margin-bottom: 12px;
}

.dc-lock-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.dc-lock-user {
  margin: 4px 0 20px;
  font-size: 15px;
  color: #475569;
}

.dc-lock-label {
  display: block;
  text-align: left;
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;
}

.dc-lock-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 15px;
}

.dc-lock-input--pin {
  letter-spacing: 10px;
  text-align: center;
  font-size: 22px;
}

/* Applied only where CSS.supports confirmed it, so the field is never left
   showing the digits on a browser that ignores the property. */
.dc-masked {
  -webkit-text-security: disc;
}

.dc-lock-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: #dc2626;
}

.dc-lock-btn {
  width: 100%;
  margin-top: 16px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
}

.dc-lock-btn--primary {
  background: #2563eb;
  color: #fff;
}

.dc-lock-btn--primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.dc-lock-link {
  display: block;
  width: 100%;
  margin-top: 12px;
  background: none;
  border: none;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
}

.dc-lock-link--switch {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
}

.dc-lock-link--endshift {
  margin-top: 4px;
  color: #b91c1c;
}

.dc-lock-roster {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin: 8px 0 20px;
}

.dc-lock-chip {
  padding: 6px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  font-size: 14px;
  color: #334155;
  cursor: pointer;
}

.dc-lock-chip--active {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
}

.dc-lock-chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
